import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';

import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { MaskService } from '../../../../../../shared/services/mask.service';
import { ConciliacaoBancariaService } from '../../../../../../shared/services/apis/financas/conciliacao-bancaria.service';
import { ExtratoBancarioItemStruct } from '../../../../../../shared/services/structs/financas/extrato-bancario-item.struct';
import { RegistroAssociavelItemStruct } from '../../../../../../shared/services/structs/financas/registro-associavel-item.struct';
import { RevisarEConciliarRequest } from '../../../../../../shared/services/requests/financas/revisar-e-conciliar.request';
import { ItemRevisadoStruct } from '../../../../../../shared/services/structs/financas/item-revisado.struct';
import { TipoMatchConciliacaoEnum } from '../../../../../../shared/enums/financas/tipo-match-conciliacao.enum';
import { UtilService } from '../../../../../../shared/services/util.service';
import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';
import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';

@Component({
  selector: 'app-revisar-valores',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    AbntPipe,
    AlertCardComponent,
  ],
  templateUrl: './revisar-valores.component.html',
  styleUrls: ['./revisar-valores.component.css'],
})
export class RevisarValoresComponent implements OnInit, OnChanges {
  @Input() extrato!: ExtratoBancarioItemStruct;
  @Input() idContaFinanceira!: number;
  @Input() itensSelecionados!: RegistroAssociavelItemStruct[];

  @Output() conciliado = new EventEmitter<void>();
  @Output() voltar = new EventEmitter<void>();

  isSalvando = false;
  formArray!: FormArray;
  currencyMask: any;

  readonly Math = Math;

  constructor(
    private fb: FormBuilder,
    private maskService: MaskService,
    private conciliacaoBancariaService: ConciliacaoBancariaService,
    private alertService: AlertService,
  ) {
    this.currencyMask = this.maskService.getCurrencyMask();
  }

  ngOnInit(): void {
    this.rebuiltFormArray();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reconstrói o formulário quando os itens mudam (troca associar → revisar)
    if (changes['itensSelecionados'] && this.fb) {
      this.rebuiltFormArray();
    }
  }

  private rebuiltFormArray(): void {
    this.formArray = this.fb.array(
      (this.itensSelecionados ?? []).map(item => this.fb.group({
        valorJuros: [0],
        valorMulta: [0],
        valorDesconto: [0],
        valorTaxa: [0],
        novoValor: [item.valorEmAberto],
        valorPrincipalPago: [item.valorEmAberto]
      }))
    );
  }

  temEncargos(item: RegistroAssociavelItemStruct): boolean {
    return item.tipoMatch === TipoMatchConciliacaoEnum.ContaPagar
      || item.tipoMatch === TipoMatchConciliacaoEnum.ContaReceber
      || item.idContasPagarParcelaOrigem != null
      || item.idContasReceberParcelaOrigem != null;
  }

  temBaixaParcial(item: RegistroAssociavelItemStruct): boolean {
    return item.tipoMatch === TipoMatchConciliacaoEnum.ContaPagar
      || item.tipoMatch === TipoMatchConciliacaoEnum.ContaReceber;
  }

  tipoLabel(item: RegistroAssociavelItemStruct): string {
    switch (item.tipoMatch) {
      case TipoMatchConciliacaoEnum.Movimentacao: return 'Movimentação';
      case TipoMatchConciliacaoEnum.ContaPagar: return 'Conta a Pagar';
      case TipoMatchConciliacaoEnum.ContaReceber: return 'Conta a Receber';
      default: return '';
    }
  }

  formAt(i: number): FormGroup {
    return this.formArray.at(i) as FormGroup;
  }

  valorFinalItem(i: number): number {
    const fg = this.formAt(i);
    const item = this.itensSelecionados[i];
    if (this.temEncargos(item)) {
      const principal = this.temBaixaParcial(item)
        ? (fg.value.valorPrincipalPago ?? item.valorEmAberto)
        : item.valorEmAberto;
      return UtilService.arredondarAbnt(
        principal
        + (fg.value.valorJuros || 0)
        + (fg.value.valorMulta || 0)
        + (fg.value.valorTaxa || 0)
        - (fg.value.valorDesconto || 0),
        2);
    }
    return UtilService.arredondarAbnt(fg.value.novoValor ?? item.valorEmAberto, 2);
  }

  get totalBanco(): number {
    return UtilService.arredondarAbnt(Math.abs(this.extrato.valor), 2);
  }

  get totalAjustado(): number {
    const total = this.itensSelecionados.reduce(
      (acc, _, i) => acc + this.valorFinalItem(i), 0);
    return UtilService.arredondarAbnt(total, 2);
  }

  get diferenca(): number {
    return UtilService.arredondarAbnt(this.totalBanco - this.totalAjustado, 2);
  }

  get podeConciliar(): boolean {
    return Math.abs(this.diferenca) < 0.01;
  }

  confirmar(): void {
    if (!this.podeConciliar || this.isSalvando) return;
    this.isSalvando = true;

    const itens: ItemRevisadoStruct[] = this.itensSelecionados.map((item, i) => {
      const fg = this.formAt(i);
      if (this.temEncargos(item)) {
        return {
          tipoMatch: item.tipoMatch,
          idRegistro: item.idRegistro,
          ...(this.temBaixaParcial(item) ? { valorPrincipalPago: fg.value.valorPrincipalPago } : {}),
          valorJuros: fg.value.valorJuros || 0,
          valorMulta: fg.value.valorMulta || 0,
          valorDesconto: fg.value.valorDesconto || 0,
          valorTaxa: fg.value.valorTaxa || 0,
        };
      }
      return {
        tipoMatch: item.tipoMatch,
        idRegistro: item.idRegistro,
        valorJuros: 0,
        valorMulta: 0,
        valorDesconto: 0,
        valorTaxa: 0,
        novoValor: fg.value.novoValor ?? item.valorEmAberto,
      };
    });

    const request: RevisarEConciliarRequest = {
      idExtratoBancario: this.extrato.idExtratoBancario,
      itens,
    };

    this.conciliacaoBancariaService.revisarEConciliar(request).subscribe({
      next: res => {
        this.isSalvando = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Conciliação realizada com sucesso.', AlertType.success);
        this.conciliado.emit();
      },
      error: () => {
        this.isSalvando = false;
        this.alertService.show('Erro', 'Falha ao salvar. Tente novamente.', AlertType.error);
      },
    });
  }

  onVoltar(): void {
    this.voltar.emit();
  }
}
